<template>
  <!-- Banner and Header -->
  <header class="relative">
    <img
      alt="banner faq"
      src="/images/banner-faqs.jpg"
      class="hidden md:block w-full h-full object-cover object-center"
    />
    <img
      alt="banner faq"
      src="/images/banner-faqs.jpg"
      class="md:hidden w-full h-full object-cover object-center"
    />
    <BaseContainer>
      <h1
        class="hidden md:block absolute top-1/2 -translate-y-1/2 text-[32px] font-bold text-black z-10"
      >
        {{ $t('pages.aboutUs.aboutUs') }}
      </h1>
    </BaseContainer>
    <BaseContainer>
      <p
        class="md:hidden absolute top-1/2 -translate-y-1/2 text-[32px] font-bold text-black z-10"
      >
        {{ $t('pages.aboutUs.aboutUs') }}
      </p>
    </BaseContainer>
  </header>

  <BaseContainer
    class="grid md:grid-cols-2 my-10 md:my-[112px] md:gap-x-[176px]"
  >
    <div>
      <SunEditorRenderer :value="title[locale as keyof typeof content]" />
    </div>
    <div>
      <SunEditorRenderer :value="content[locale as keyof typeof content]" />
    </div>
  </BaseContainer>

  <BaseContainer class="max-lg:px-0">
    <section class="grid md:grid-cols-2 mt-10 md:mt-[112px] h-auto">
      <img
        alt="about us 1"
        src="/images/about-us4.jpg"
        class="object-cover h-full w-full max-lg:h-[300px]"
      />
      <img
        alt="about us 2"
        src="/images/about-us5.jpg"
        class="object-cover h-full w-full max-lg:h-[300px]"
      />
    </section>

    <section
      class="grid md:grid-cols-1 h-[400px] max-md:h-auto max-md:flex max-md:flex-col-reverse max-lg:h-[326px] overflow-hidden"
    >
      <div
        class="bg-black text-white flex flex-col justify-center px-[48px] h-full space-y-4 max-lg:px-[24px] max-lg:h-[326px]"
      >
        <h2 class="text-[32px] max-lg:text-[24px]">
          {{ $t('pages.aboutUs.ourStatement') }}
        </h2>
        <p class="text-[16px] max-lg:text-[14px]">
          {{ $t('pages.aboutUs.ourStatementDescription') }}
        </p>
      </div>

      <!-- <img
        alt="about us 3"
        src="/images/about-us3.jpg"
        class="object-cover h-full w-full max-lg:h-[356px]"
      /> -->
    </section>
  </BaseContainer>

  <BaseContainer
    class="relative h-[680px] flex items-center justify-center max-lg:h-[740px] overflow-hidden"
  >
    <img
      alt="about us case 1"
      src="/images/about-us-case1.png"
      class="absolute rotate-[22deg] top-50 -left-24 w-[350px] h-[350px] max-lg:w-[200px] max-lg:h-[200px] max-md:-left-18 max-md:rotate-[37deg] max-lg:top-20 object-contain"
    />
    <img
      alt="about us case 2"
      src="/images/about-us-case2.png"
      class="absolute -rotate-[24deg] bottom-[-160px] left-40 w-[350px] h-[350px] max-lg:w-[200px] max-lg:h-[200px] max-md:left-4 max-md:-rotate-[24deg] max-lg:bottom-[-90px] object-contain"
    />
    <img
      alt="about us case 3"
      src="/images/about-us-case3.png"
      class="absolute rotate-[22deg] bottom-[-160px] right-10 w-[350px] h-[350px] z-10 max-lg:w-[200px] max-lg:h-[200px] max-md:-right-20 max-md:rotate-[19deg] max-lg:bottom-[30px] object-contain"
    />
    <img
      alt="about us case 4"
      src="/images/about-us-case4.png"
      class="absolute -rotate-[24deg] top-60 -right-32 w-[350px] h-[350px] max-lg:w-[200px] max-lg:h-[200px] max-md:-right-20 max-md:-rotate-[21deg] max-lg:top-10 object-contain"
    />

    <section class="w-[728px] flex flex-col items-center text-center">
      <p class="text-[20px]">{{ $t('pages.aboutUs.whyUs') }}</p>
      <p
        class="text-[32px] font-bold max-lg:text-[24px] max-lg:font-medium max-lg:mt-4"
      >
        {{ $t('pages.aboutUs.whyUsDescription') }}
      </p>
      <Button
        mobile
        icon="arrow-up-right"
        class="lg:min-w-[240px] !rounded-[16px] mt-2"
        @click="router.push(localePath('/styles'))"
      >
        {{ $t('pages.aboutUs.whyUsShopNow') }}
      </Button>
    </section>
  </BaseContainer>

  <CaseClubSocial />
</template>

<script lang="ts" setup>
import aboutUsTitleEn from '~/assets/content/aboutUs/aboutUsTitleEn'
import aboutUsTitleTh from '~/assets/content/aboutUs/aboutUsTitleTh'
import aboutUsEn from '~/assets/content/aboutUs/aboutUsEn'
import aboutUsTh from '~/assets/content/aboutUs/aboutUsTh'

import { seoRepository } from '~/repository/seoRepository'

const { locale, t } = useI18n()
const { $serverApi } = useNuxtApp()
const localePath = useLocalePath()
const router = useRouter()
const route = useRoute()

// 🔥 Import Braze tracking
const { trackStaticPageView } = useCaseClubEvents()

const seoRepo = seoRepository($serverApi)

// Fetch SEO Metadata
const { data: pageDataSeo } = await useAsyncData(
  'getStaticSeoAboutUs',
  async () => {
    const data = await seoRepo.getSeo('about-us')
    return data
  }
)

// 🔥 Track static page view on mount
onMounted(() => {
  try {
    trackStaticPageView({
      page_slug: 'about-us',
      page_name: 'About Us',
      url: window.location.href
    })
  } catch (error) {
  }
})

const seoData = computed(() => {
  const defaultSeo = {
    title: locale.value === 'en' ? pageDataSeo.value?.meta_title?.en : pageDataSeo.value?.meta_title?.th,
    description: locale.value === 'en' ? pageDataSeo.value?.meta_description?.en : pageDataSeo.value?.meta_description?.th,
    ogImage: pageDataSeo.value?.og_image,
    keywords: pageDataSeo.value?.meta_keyword
  }

  return defaultSeo
})

useSeoMeta({
  title: seoData.value.title || undefined,
  description: seoData.value.description || undefined,
  ogImage: seoData.value.ogImage ? String(seoData.value.ogImage) : undefined,
  keywords: seoData.value.keywords || undefined
})

const title = {
  en: aboutUsTitleEn,
  th: aboutUsTitleTh
}

const content = {
  en: aboutUsEn,
  th: aboutUsTh
}
</script>
